import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// The one Kanban board. Lanes, drag and drop, lane headers with counts and
// the empty state live here; the page supplies its lanes, its items and how a
// card is drawn. Talent Acquisition and the ECR Tracker both render this, so
// a third board is a configuration rather than another copy of the drag code.
//
// Props:
//   lanes        — [{ key, label, group?, description?, flag? }] in board order
//   groups       — optional [{ key, label }]; lanes are shown under a group
//                  heading in this order (the ECR pre-work / delivery / post-work)
//   items        — the cards
//   laneOf       — (item) => lane key
//   itemKey      — (item) => stable key (default item.id)
//   renderCard   — (item) => node; the page owns the card
//   onMove       — (itemId, laneKey) => void, only when the lane changes
//   canDrag      — optional (item) => bool (default true)
//   onSelect     — optional (item) => void on click
//   selectedKey  — key of the highlighted card, if any
//   layout       — 'scroll' (default, one row that scrolls sideways) or 'grid'
//   className    — container classes when layout is 'grid'
//   laneWidth    — px min width of a lane in scroll layout (default 220)
//   dataTour     — [data-tour] anchor on the container
//   emptyLabel   — text shown in an empty lane
//   focusKey     — pass the page's search and filter state; whenever it
//                  changes, the first lane that still has cards is scrolled
//                  into view, so a search hit is never off the edge of a
//                  board that was scrolled to the other end
//
// In scroll layout the board is cut to the height of the window, lanes scroll
// their own cards, so the sideways scrollbar is on screen without scrolling the
// page. Pressing and dragging empty board space with the mouse pans it
// sideways; a press on a card is left to the card's own drag and click.
export default function KanbanBoard({
  lanes, groups, items, laneOf, itemKey = (i) => i.id, renderCard,
  onMove, canDrag = () => true, onSelect, selectedKey,
  layout = 'scroll', className = '', laneWidth = 220, dataTour, emptyLabel = 'Empty', focusKey,
}) {
  const dragKey = useRef(null);
  const [over, setOver] = useState(null);
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState();

  useLayoutEffect(() => {
    if (layout !== 'scroll') return;
    const el = containerRef.current;
    if (!el) return;
    // The app shell scrolls its content area, not the window.
    let scroller = el.parentElement;
    while (scroller && !/auto|scroll/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    const fit = () => {
      if (!el.offsetHeight) return;
      const r = el.getBoundingClientRect();
      // The S/M/L text size is CSS zoom: rects are screen pixels, maxHeight is CSS pixels.
      const zoom = r.height / el.offsetHeight;
      const top = r.top + (scroller?.scrollTop ?? 0) * zoom;
      setMaxHeight(Math.max(320, (window.innerHeight - top) / zoom - 16));
    };
    fit();
    // Watching the scroller catches a window resize and a text size change alike.
    const ro = new ResizeObserver(fit);
    ro.observe(scroller || document.documentElement);
    return () => ro.disconnect();
  }, [layout]);

  const startPan = (e) => {
    const t = e.target;
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    // A card, a control or a scrollbar (the strip outside clientWidth/Height) keeps its own behaviour.
    if (t.closest('[data-card], button, a, input, select, textarea')) return;
    if (e.nativeEvent.offsetX > t.clientWidth || e.nativeEvent.offsetY > t.clientHeight) return;
    const el = containerRef.current;
    const startX = e.clientX, startLeft = el.scrollLeft;
    e.preventDefault();
    const move = (m) => { el.scrollLeft = startLeft - (m.clientX - startX); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const byLane = {};
  for (const l of lanes) byLane[l.key] = [];
  for (const it of items) {
    const k = laneOf(it);
    if (byLane[k]) byLane[k].push(it);
  }

  const firstLaneWithCards = lanes.find(l => byLane[l.key]?.length)?.key ?? null;
  useEffect(() => {
    if (focusKey === undefined || !firstLaneWithCards || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-lane="${firstLaneWithCards}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    // Only when the filter changes, not on every reload of the same view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const drop = (laneKey) => (e) => {
    e.preventDefault();
    const id = dragKey.current;
    dragKey.current = null;
    setOver(null);
    if (id == null) return;
    const item = items.find(i => itemKey(i) === id);
    if (!item || laneOf(item) === laneKey) return;
    onMove?.(id, laneKey);
  };

  const laneEl = (lane) => {
    const laneItems = byLane[lane.key] || [];
    const isOver = over === lane.key;
    return (
      <div key={lane.key} data-lane={lane.key}
        className={`bg-gray-100 rounded-lg p-2 min-h-[200px] flex flex-col transition ${isOver ? 'ring-2 ring-gold/50' : ''} ${layout === 'scroll' ? 'flex-shrink-0' : ''}`}
        style={layout === 'scroll' ? { width: laneWidth } : undefined}
        onDragOver={(e) => { e.preventDefault(); if (over !== lane.key) setOver(lane.key); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }}
        onDrop={drop(lane.key)}>
        <div className="mb-2">
          <div className="flex items-start justify-between gap-1 text-[10px] font-bold uppercase tracking-wide text-gray-600">
            <span title={lane.description || ''}>{lane.label}</span>
            <span className="bg-white rounded-full px-1.5 text-navy flex-shrink-0">{laneItems.length}</span>
          </div>
          {lane.flag && <div className="text-[9px] font-bold uppercase tracking-wide text-gold mt-0.5">{lane.flag}</div>}
        </div>
        <div data-lane-cards className={layout === 'scroll' ? 'flex-1 min-h-0 overflow-y-auto' : ''}>
        {laneItems.map(it => {
          const key = itemKey(it);
          const draggable = canDrag(it);
          return (
            <div key={key} data-card draggable={draggable}
              onDragStart={() => { dragKey.current = key; }}
              onDragEnd={() => { dragKey.current = null; setOver(null); }}
              onClick={onSelect ? () => onSelect(it) : undefined}
              className={`mb-2 ${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${onSelect ? 'cursor-pointer' : ''}`}
              data-selected={selectedKey != null && selectedKey === key ? 'true' : undefined}>
              {renderCard(it, { selected: selectedKey != null && selectedKey === key })}
            </div>
          );
        })}
        {laneItems.length === 0 && <div className="text-[9px] text-gray-400 text-center py-4">{emptyLabel}</div>}
        </div>
      </div>
    );
  };

  if (layout === 'grid') {
    return (
      <div ref={containerRef} data-tour={dataTour} className={className}>
        {lanes.map(laneEl)}
      </div>
    );
  }

  const scrollProps = {
    ref: containerRef, 'data-tour': dataTour, onPointerDown: startPan, style: { maxHeight },
  };

  if (groups && groups.length) {
    return (
      <div {...scrollProps} className="flex gap-3 overflow-x-auto pb-2 items-stretch cursor-grab">
        {groups.map(g => {
          const groupLanes = lanes.filter(l => l.group === g.key);
          if (!groupLanes.length) return null;
          return (
            <section key={g.key} className="flex-shrink-0 flex flex-col">
              <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold mb-1.5 px-1 flex items-center gap-2">
                <span>{g.label}</span>
                <span className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="flex-1 min-h-0 flex gap-2 items-stretch">
                {groupLanes.map(laneEl)}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div {...scrollProps} className="flex gap-2 overflow-x-auto pb-2 items-stretch cursor-grab">
      {lanes.map(laneEl)}
    </div>
  );
}
